import './ReportFormView.scss';
import { HvacGroup, HvacResults, Uuid } from '@belimo-retrofit-portal/logic';
import { useForm } from '@form-ts/react';
import { ReadonlyRecord } from 'fp-ts/ReadonlyRecord';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useAction } from 'src/hooks/useAction';
import { Button } from 'src/modules/common/components/Button';
import { ArrowLeftIcon } from 'src/modules/common/icons/ArrowLeftIcon';
import { FileIcon } from 'src/modules/common/icons/FileIcon';
import { PdfMetadata } from 'src/modules/common/types/PdfMetadata';
import { Project } from 'src/modules/common/types/Project';
import { UserSettings } from 'src/modules/common/types/UserSettings';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { REPORT_CHANGE, REPORT_GENERATE } from 'src/modules/report/actions/ReportActions';
import { REPORT_FORM_SCHEMA } from 'src/modules/report/constants/schema';
import { ReportAttachment } from 'src/modules/report/types/ReportAttachment';
import { ReportContentPage } from 'src/modules/report/types/ReportContent';
import { ReportFormData } from 'src/modules/report/types/ReportFormData';
import { ReportGenerated } from 'src/modules/report/types/ReportGenerated';
import { ReportFormDocument } from 'src/modules/report/views/ReportFormDocument';
import { ReportFormSectionConfigAppendix } from 'src/modules/report/views/ReportFormSectionConfigAppendix';
import { ReportFormSectionConfigCover } from 'src/modules/report/views/ReportFormSectionConfigCover';
import { ReportFormSectionConfigEnergy } from 'src/modules/report/views/ReportFormSectionConfigEnergy';
import { ReportFormSectionConfigEvaluation } from 'src/modules/report/views/ReportFormSectionConfigEvaluation';
import {
  ReportFormSectionConfigEvaluationCharts,
} from 'src/modules/report/views/ReportFormSectionConfigEvaluationCharts';
import { ReportFormSectionConfigHvac } from 'src/modules/report/views/ReportFormSectionConfigHvac';
import { ReportFormSectionConfigSavings } from 'src/modules/report/views/ReportFormSectionConfigSavings';
import { ReportFormSectionHvacEmpty } from 'src/modules/report/views/ReportFormSectionHvacEmpty';
import { ReportFormSectionPreview } from 'src/modules/report/views/ReportFormSectionPreview';
import { Language } from 'src/types/common/Language';

type Props = {
  readonly language: Language;
  readonly project: Project;
  readonly document: ReportGenerated;
  readonly formData: ReportFormData;
  readonly loading: boolean;
  readonly embeddings: ReadonlyRecord<Uuid, PdfMetadata>;
  readonly attachments: ReadonlyArray<ReportAttachment>;
  readonly userSettings: UserSettings;
  readonly useOrganizationContacts: boolean;
  readonly readOnly: boolean;
  readonly updateUserSettings: (data: UserSettings) => void;
  readonly onUploadAttachment: (file: File) => void;
  readonly onDeleteAttachment: (attachment: ReportAttachment) => void;
  readonly results: HvacResults;
};

export const ReportFormView = ({
  language,
  project,
  document,
  formData,
  loading,
  embeddings,
  attachments,
  userSettings,
  useOrganizationContacts,
  readOnly,
  updateUserSettings,
  onUploadAttachment,
  onDeleteAttachment,
  results,
}: Props): React.ReactElement => {
  const projectEvaluationUrl = `/project/${encodeURIComponent(project.id)}/evaluation`;

  const form = useForm<ReportFormData, FormError>('project.report', {
    reinitialize: false,
    initialValues: formData,
  });
  useFormValidator(form, REPORT_FORM_SCHEMA);

  const saveForm = useAction(REPORT_CHANGE.trigger);
  useEffect(() => form.subscribe((prevState) => {
    const nextState = form.currentState;

    if (prevState.values !== nextState.values) {
      saveForm(nextState.values);
    }
  }), [form, saveForm]);

  const generateReport = useAction(REPORT_GENERATE.request);
  const handleReportClick = useCallback(() => {
    generateReport(form.currentState.values);
  }, [form, generateReport]);

  const [reportPages, setReportPages] = useState<ReadonlyArray<ReportContentPage>>([]);

  return (
    <div className="bp-report-form-view">
      <div className="bp-report-form-view__wrapper">
        <div className="bp-report-form-view__actions">
          <Button
            disabled={loading}
            className="bp-report-form-view__actions-button"
            kind="primary"
            onClick={handleReportClick}
            loading={loading}
            size="md"
            icon={(
              <div className="bp-report-form-view__action-icon">
                <FileIcon/>
              </div>
            )}
          >
            <FormattedMessage id="report/form/generateReport"/>
          </Button>
        </div>

        <ReportFormDocument
          results={results}
          document={document}
          formData={formData}
          onChange={setReportPages}
          project={project}
          language={language}
          userSettings={userSettings}
          embeddings={embeddings}
        />

        <div className="bp-report-form-view__content">
          <div className="bp-report-form-view__section">
            <div className="bp-report-form-view__section-preview">
              <ReportFormSectionPreview
                pages={reportPages.filter((it) => it.section === 'cover')}
                enabled={true}
              />
            </div>
            {!readOnly && (
              <div className="bp-report-form-view__section-config">
                <ReportFormSectionConfigCover
                  field={form.field.at('cover')}
                  project={project}
                  document={document}
                  userSettings={userSettings}
                  useOrganizationContacts={useOrganizationContacts}
                  updateUserSettings={updateUserSettings}
                />
              </div>
            )}
          </div>

          <div className="bp-report-form-view__section">
            <div className="bp-report-form-view__section-preview">
              <ReportFormSectionPreview
                pages={reportPages.filter((it) => it.section === 'savings')}
                enabled={formData.savings.enabled}
              />
            </div>
            {!readOnly && (
              <div className="bp-report-form-view__section-config">
                <ReportFormSectionConfigSavings
                  field={form.field.at('savings')}
                  project={project}
                />
              </div>
            )}
          </div>

          <div className="bp-report-form-view__section">
            <div className="bp-report-form-view__section-preview">
              <ReportFormSectionPreview
                pages={reportPages.filter((it) => it.section === 'evaluation')}
                enabled={formData.evaluation.enabled}
              />
            </div>
            {!readOnly && (
              <div className="bp-report-form-view__section-config">
                <ReportFormSectionConfigEvaluation
                  field={form.field.at('evaluation')}
                  project={project}
                />
              </div>
            )}
          </div>

          <div className="bp-report-form-view__section">
            <div className="bp-report-form-view__section-preview">
              <ReportFormSectionPreview
                pages={reportPages.filter((it) => it.section === 'evaluationCharts')}
                enabled={formData.evaluationCharts.enabled}
              />
            </div>
            {!readOnly && (
              <div className="bp-report-form-view__section-config">
                <ReportFormSectionConfigEvaluationCharts
                  field={form.field.at('evaluationCharts')}
                  project={project}
                />
              </div>
            )}
          </div>

          <div className="bp-report-form-view__section">
            <div className="bp-report-form-view__section-preview">
              <ReportFormSectionPreview
                pages={reportPages.filter((it) => it.section === 'energy')}
                enabled={formData.energy.enabled}
              />
            </div>

            {!readOnly && (
              <div className="bp-report-form-view__section-config">
                <ReportFormSectionConfigEnergy
                  field={form.field.at('energy')}
                  project={project}
                />
              </div>
            )}
          </div>

          {document.hvac.map((groupData, groupIndex) => (
            <div key={groupData.id} className="bp-report-form-view__section">
              <div className="bp-report-form-view__section-preview">
                {groupData.empty && (
                  <ReportFormSectionHvacEmpty
                    group={groupData.group}
                    project={project}
                  />
                )}
                {!groupData.empty && (
                  <ReportFormSectionPreview
                    pages={reportPages.filter((it) => it.section === `hvac-${groupData.id}`)}
                    enabled={groupData.enabled}
                  />
                )}
              </div>
              {!readOnly && (
                <div className="bp-report-form-view__section-config">
                  <ReportFormSectionConfigHvac
                    field={form.field.at('hvac').at(groupIndex)}
                    project={project}
                    group={groupData}
                  />
                </div>
              )}
            </div>
          ))}

          <div className="bp-report-form-view__section">
            <div className="bp-report-form-view__section-preview">
              <ReportFormSectionPreview
                pages={reportPages.filter((it) => it.section === 'appendix')}
                enabled={formData.appendix.enabled}
              />
            </div>
            {!readOnly && (
              <div className="bp-report-form-view__section-config">
                <ReportFormSectionConfigAppendix
                  field={form.field.at('appendix')}
                  attachments={attachments}
                  onUpload={onUploadAttachment}
                  onDelete={onDeleteAttachment}
                />
              </div>
            )}
          </div>
        </div>

        <div className="bp-report-form-view__actions">
          <Button
            as={Link}
            to={projectEvaluationUrl}
            size="md"
            kind="ghost"
          >
            <div className="bp-report-form-view__action-icon">
              <ArrowLeftIcon/>
            </div>

            <FormattedMessage id="report/navigation/back"/>
          </Button>

          <Button
            disabled={loading}
            className="bp-report-form-view__actions-button"
            kind="primary"
            onClick={handleReportClick}
            loading={loading}
            size="md"
            icon={(
              <div className="bp-report-form-view__action-icon">
                <FileIcon/>
              </div>
            )}
          >
            <FormattedMessage id="report/form/generateReport"/>
          </Button>
        </div>
      </div>
    </div>
  );
};

const GROUP_TITLES = {
  [HvacGroup.EMISSION]: <FormattedMessage id="report/document/hvac/group/emission"/>,
  [HvacGroup.DISTRIBUTION]: <FormattedMessage id="report/document/hvac/group/distribution"/>,
  [HvacGroup.AIR_HANDLER]: <FormattedMessage id="report/document/hvac/group/air_handler"/>,
  [HvacGroup.GENERATION]: <FormattedMessage id="report/document/hvac/group/generation"/>,
};
